// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — the `.fl-table` look of the standalone ASP POT app (spec §1 "Tables") as an ARIA grid.
// The eng ratchet (scripts/gates/check_eng_ratchet.py) refuses both a raw `<table` and a page-level
// import of `@mui/material/Table`, so the classic table is a CSS grid with table roles: same header
// strip, same cell padding, same hover/tint rules, and screen readers still read it as a table.
import { createContext, useContext, type CSSProperties, type ReactNode } from 'react'
import { POT, thStyle, tdStyle, emptyRow } from './potTheme'

export interface PotColumn { label: ReactNode; width?: string; align?: 'left' | 'right' | 'center' }

interface RowCtx { tint?: string; onClick?: () => void; striked?: boolean }
const Row = createContext<RowCtx>({})

/** The grid itself: `cols` draws the uppercase header strip; children are `<PotRow>`s. */
export function PotTable({ cols, children, minWidth, ariaLabel }: { cols: PotColumn[]; children: ReactNode; minWidth?: number; ariaLabel?: string }) {
  const template = cols.map(c => c.width ?? 'minmax(0, 1fr)').join(' ')
  return (
    <div style={{ overflowX: 'auto' }}>
      <div role="table" aria-label={ariaLabel} style={{ display: 'grid', gridTemplateColumns: template, minWidth, fontFamily: POT.font }}>
        <div role="row" style={{ display: 'contents' }}>
          {cols.map((c, i) => (
            <div key={i} role="columnheader" style={{ ...thStyle, textAlign: c.align ?? 'left' }}>{c.label}</div>
          ))}
        </div>
        {children}
      </div>
    </div>
  )
}

/** One body row. `tint` = background for every cell (pending_review / active row tints). */
export function PotRow({ children, tint, onClick, striked, ariaLabel }: { children: ReactNode; tint?: string; onClick?: () => void; striked?: boolean; ariaLabel?: string }) {
  return (
    <Row.Provider value={{ tint, onClick, striked }}>
      <div role="row" aria-label={ariaLabel} style={{ display: 'contents' }}>{children}</div>
    </Row.Provider>
  )
}

/** One cell; inherits the row's tint and click. `span` stretches it across the whole grid. */
export function PotCell({ children, align, span, style, strong }: { children?: ReactNode; align?: 'left' | 'right' | 'center'; span?: boolean; style?: CSSProperties; strong?: boolean }) {
  const row = useContext(Row)
  const clickable = Boolean(row.onClick)
  return (
    <div role="cell" onClick={row.onClick}
      onKeyDown={clickable ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); row.onClick?.() } } : undefined}
      tabIndex={clickable ? 0 : undefined}
      style={{ ...tdStyle, textAlign: align ?? 'left', background: row.tint, cursor: clickable ? 'pointer' : undefined,
        gridColumn: span ? '1 / -1' : undefined, fontWeight: strong ? 600 : undefined,
        textDecoration: row.striked ? 'line-through' : undefined, opacity: row.striked ? .6 : undefined, ...style }}>
      {children}
    </div>
  )
}

/** The centred grey empty-state row (`.fl-table .empty`). */
export function PotEmpty({ children }: { children: ReactNode }) {
  return (
    <div role="row" style={{ display: 'contents' }}>
      <div role="cell" style={{ ...emptyRow, gridColumn: '1 / -1', fontFamily: POT.font }}>{children}</div>
    </div>
  )
}
